import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Box, IconButton } from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SECONDARY_SIDEBAR_COLLAPSED_WIDTH_PX,
  SECONDARY_SIDEBAR_EXPANDED_WIDTH_PX,
} from "@/constants";
import { useSecondarySidebar } from "@/shared/ui/layout/useSecondarySidebar";
import { useSidebarCollapse } from "@/shared/ui/layout/useSidebarCollapse";

type SecondarySidebarProps = {
  enabled: boolean;
};

export function SecondarySidebar({ enabled }: SecondarySidebarProps) {
  const { t } = useTranslation("common");
  const { content } = useSecondarySidebar();
  const { forceCollapsed } = useSidebarCollapse();
  const [userExpanded, setUserExpanded] = useState(true);
  const expanded = !forceCollapsed && userExpanded;

  const toggleExpanded = useCallback(() => {
    setUserExpanded((value) => !value);
  }, []);

  if (!enabled) {
    return null;
  }

  const widthPx = expanded
    ? SECONDARY_SIDEBAR_EXPANDED_WIDTH_PX
    : SECONDARY_SIDEBAR_COLLAPSED_WIDTH_PX;

  return (
    <Box
      component="aside"
      aria-label={t("secondarySidebar.ariaLabel")}
      sx={{
        position: "relative",
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: widthPx,
        minWidth: widthPx,
        overflow: "visible",
        transition: (theme) =>
          theme.transitions.create(["width", "min-width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        bgcolor: "var(--theme-surface)",
        borderRight: "1px solid var(--theme-border)",
      }}
    >
      <IconButton
        size="small"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-label={
          expanded ? t("secondarySidebar.collapse") : t("secondarySidebar.expand")
        }
        sx={{
          position: "absolute",
          right: expanded ? 4 : -12,
          top: 20,
          zIndex: 1,
          bgcolor: "transparent",
          color: "var(--theme-text-secondary)",
          "&:hover": {
            bgcolor: "var(--theme-hover-secondary)",
            color: "var(--theme-text-primary)",
          },
        }}
      >
        {expanded ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
      </IconButton>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: expanded ? "flex" : "none",
          flexDirection: "column",
        }}
      >
        {content}
      </Box>
    </Box>
  );
}
